import type { CreateResumeDto, ResumeDto } from "@reactive-resume/dto";
import { DEFAULT_TEMPLATE } from "@reactive-resume/utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useCreateResume } from "@/client/services/resume/create";
import { useMyResumes } from "@/client/services/resume/my-resumes";
import { useUser } from "@/client/services/user";

import { useFormResumeCreate } from "../../../dashboard/_components/form-create";
import { DashboardContent } from "../../components/layout/dashboard-content";
import { TemplatePickerDialog } from "../../templates/components/template-picker-dialog";
import type { DocumentTemplate } from "../../templates/data/templates";
import { DocumentGrid } from "../components/document-grid";
import { DocumentsPageHeader } from "../components/documents-page-header";
import { OfferCard } from "../components/offer-card";
import type { DocumentSort } from "../types/document";
import { ResumeCard } from "./resume-card";
import { ResumeCreateCard } from "./resume-create-card";

const sortResumes = (resumes: ResumeDto[], sort: DocumentSort) => {
  return [...resumes].sort((first, second) => {
    switch (sort) {
      case "oldest":
        return new Date(first.updatedAt).getTime() - new Date(second.updatedAt).getTime();

      case "title-ascending":
        return first.title.localeCompare(second.title, "fr", { sensitivity: "base" });

      case "title-descending":
        return second.title.localeCompare(first.title, "fr", { sensitivity: "base" });

      case "recent":
      default:
        return new Date(second.updatedAt).getTime() - new Date(first.updatedAt).getTime();
    }
  });
};

const getResumeTemplateName = (template: DocumentTemplate): CreateResumeDto["template"] => {
  const thumbnailFile = template.thumbnailUrl?.split("/").at(-1)?.replace(".webp", "");

  return (thumbnailFile ?? DEFAULT_TEMPLATE) as CreateResumeDto["template"];
};

const createResumeSlug = (template: DocumentTemplate) => {
  const normalizedName = template.name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return `cv-${normalizedName}-${Date.now()}`;
};

const LoadingCard = () => {
  return (
    <div
      aria-hidden
      className="min-h-[216px] animate-pulse rounded-2xl border border-slate-200 bg-white p-3 sm:min-h-[232px]"
    >
      <div className="h-36 rounded-xl bg-slate-100 sm:h-40" />

      <div className="mt-3 h-3 w-2/3 rounded-full bg-slate-100" />

      <div className="mt-2 h-2.5 w-1/2 rounded-full bg-slate-100" />
    </div>
  );
};

export const ResumesPage = () => {
  const navigate = useNavigate();

  const { user } = useUser();

  const { resumes, loading, error } = useMyResumes(user?.id);

  const { createResume, loading: creationLoading } = useCreateResume();

  const creationForm = useFormResumeCreate();

  const [sort, setSort] = useState<DocumentSort>("recent");

  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const sortedResumes = useMemo(() => sortResumes(resumes ?? [], sort), [resumes, sort]);

  const firstResume = sortedResumes.at(0);

  const remainingResumes = sortedResumes.slice(1);

  const openResumeCreation = () => {
    creationForm?.setformCreateResume(true);
  };

  // `?create=1` ouvre le formulaire « Nouveau CV » (liens des fenêtres de notification).
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("create") !== "1" || !creationForm) return;

    creationForm.setformCreateResume(true);
    setSearchParams(
      (params) => {
        params.delete("create");
        return params;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, creationForm]);

  const handleUseTemplate = async (template: DocumentTemplate) => {
    if (creationLoading) {
      return;
    }

    const resume = await createResume({
      title: `CV ${template.name}`,
      slug: createResumeSlug(template),
      visibility: "private",
      template: getResumeTemplateName(template),
    });

    await navigate(`/client/builder/${resume.id}`);
  };

  return (
    <DashboardContent>
      <section className="min-w-0 space-y-3.5 sm:space-y-4">
        <DocumentsPageHeader
          title="Mes CVs"
          description="Créez, organisez et mettez à jour vos CVs professionnels."
          sort={sort}
          onSortChange={setSort}
        />

        {error ? (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
            <p className="text-xs font-semibold text-rose-800 sm:text-[13px]">
              Impossible de charger vos CVs
            </p>

            <p className="mt-1 text-[11px] leading-4 text-rose-700 sm:text-xs">
              Vérifiez que le serveur fonctionne sur le port 3001, puis actualisez la page.
            </p>
          </div>
        ) : (
          <DocumentGrid>
            <ResumeCreateCard
              onCreate={openResumeCreation}
              onUseTemplate={() => {
                setTemplatePickerOpen(true);
              }}
            />

            {loading ? (
              <>
                <LoadingCard />

                <OfferCard className="!min-h-[216px] sm:!min-h-[232px]" />
              </>
            ) : (
              <>
                {firstResume && <ResumeCard resume={firstResume} />}

                <OfferCard className="!min-h-[216px] sm:!min-h-[232px]" />

                {remainingResumes.map((resume) => (
                  <ResumeCard key={resume.id} resume={resume} />
                ))}
              </>
            )}
          </DocumentGrid>
        )}
      </section>

      <TemplatePickerDialog
        open={templatePickerOpen}
        kind="resume"
        onOpenChange={setTemplatePickerOpen}
        onUseTemplate={(template) => {
          void handleUseTemplate(template);
        }}
      />
    </DashboardContent>
  );
};
